import { DetailedSensor } from '@app/app/sensors/DetailedSensor';
import { Degrees, Kilometers, Milliseconds, SpaceObjectType, ZoomValue } from '@ootk/src/main';
import { Operators, SensorList } from './types';

/**
 * ESA Tracking Station Network (ESTRACK). Core-network stations handle
 * LEO/GEO TT&C; the three 35m deep-space antennas (DSA 1-3) support
 * interplanetary missions. Cooperative tracking only.
 *
 * https://www.esa.int/Enabling_Support/Operations/ESA_ground_stations
 */
export const estrackSensors = <SensorList>{
  ESTKIR: new DetailedSensor({
    objName: 'ESTKIR',
    shortName: 'KIR',
    id: 0,
    name: 'ESTRACK Kiruna Station, Salmijarvi, Sweden',
    uiName: 'Kiruna (KIR)',
    system: 'ESTRACK Core Network',
    freqBand: 'S/X-Band',
    type: SpaceObjectType.GROUND_SENSOR_STATION,
    lat: <Degrees>67.8571,
    lon: <Degrees>20.9642,
    alt: <Kilometers>0.402,
    minAz: <Degrees>0,
    maxAz: <Degrees>360,
    minEl: <Degrees>5,
    maxEl: <Degrees>90,
    minRng: <Kilometers>100,
    maxRng: <Kilometers>5000, // Primarily LEO/polar mission support
    beamwidth: <Degrees>0.6, // 15m dish at S-band
    zoom: ZoomValue.LEO,
    changeObjectInterval: <Milliseconds>20000,
    url: 'https://www.esa.int/Enabling_Support/Operations/ESA_ground_stations',
    country: 'Sweden',
    operator: Operators.ESA,
  }),
  ESTRED: new DetailedSensor({
    objName: 'ESTRED',
    shortName: 'RED',
    id: 0,
    name: 'ESTRACK Redu Station, Belgium',
    uiName: 'Redu (RED)',
    system: 'ESTRACK Core Network',
    freqBand: 'S/Ku-Band',
    type: SpaceObjectType.GROUND_SENSOR_STATION,
    lat: <Degrees>50.0019,
    lon: <Degrees>5.1457,
    alt: <Kilometers>0.386,
    minAz: <Degrees>0,
    maxAz: <Degrees>360,
    minEl: <Degrees>5,
    maxEl: <Degrees>90,
    minRng: <Kilometers>100,
    maxRng: <Kilometers>45000, // GEO in-orbit testing and TT&C
    beamwidth: <Degrees>0.6,
    zoom: ZoomValue.GEO,
    changeObjectInterval: <Milliseconds>20000,
    url: 'https://www.esa.int/Enabling_Support/Operations/ESA_ground_stations',
    country: 'Belgium',
    operator: Operators.ESA,
  }),
  ESTSMA: new DetailedSensor({
    objName: 'ESTSMA',
    shortName: 'SMA',
    id: 0,
    name: 'ESTRACK Santa Maria Station, Azores, Portugal',
    uiName: 'Santa Maria (SMA)',
    system: 'ESTRACK Core Network',
    freqBand: 'S/X-Band',
    type: SpaceObjectType.GROUND_SENSOR_STATION,
    lat: <Degrees>36.9972,
    lon: <Degrees>-25.1359,
    alt: <Kilometers>0.276,
    minAz: <Degrees>0,
    maxAz: <Degrees>360,
    minEl: <Degrees>5,
    maxEl: <Degrees>90,
    minRng: <Kilometers>100,
    maxRng: <Kilometers>5000, // LEO support and launcher tracking
    beamwidth: <Degrees>0.7, // 5.5m dish; approximate
    zoom: ZoomValue.LEO,
    changeObjectInterval: <Milliseconds>20000,
    url: 'https://www.esa.int/Enabling_Support/Operations/ESA_ground_stations',
    country: 'Portugal',
    operator: Operators.ESA,
  }),
  ESTKRU: new DetailedSensor({
    objName: 'ESTKRU',
    shortName: 'KRU',
    id: 0,
    name: 'ESTRACK Kourou Station, French Guiana',
    uiName: 'Kourou (KRU)',
    system: 'ESTRACK Core Network',
    freqBand: 'S/X-Band',
    type: SpaceObjectType.GROUND_SENSOR_STATION,
    lat: <Degrees>5.2515,
    lon: <Degrees>-52.8047,
    alt: <Kilometers>0.014,
    minAz: <Degrees>0,
    maxAz: <Degrees>360,
    minEl: <Degrees>5,
    maxEl: <Degrees>90,
    minRng: <Kilometers>100,
    maxRng: <Kilometers>45000, // LEOP support from LEO through GEO transfer
    beamwidth: <Degrees>0.6,
    zoom: ZoomValue.GEO,
    changeObjectInterval: <Milliseconds>20000,
    url: 'https://www.esa.int/Enabling_Support/Operations/ESA_ground_stations',
    country: 'France',
    operator: Operators.ESA,
  }),
  ESTNNO: new DetailedSensor({
    objName: 'ESTNNO',
    shortName: 'NNO',
    id: 0,
    name: 'ESTRACK New Norcia Station (DSA 1), Australia',
    uiName: 'New Norcia (DSA 1)',
    system: 'ESTRACK Deep Space Antenna',
    freqBand: 'S/X-Band',
    type: SpaceObjectType.GROUND_SENSOR_STATION,
    lat: <Degrees>-31.0482,
    lon: <Degrees>116.1915,
    alt: <Kilometers>0.252,
    minAz: <Degrees>0,
    maxAz: <Degrees>360,
    minEl: <Degrees>6,
    maxEl: <Degrees>90,
    minRng: <Kilometers>100,
    maxRng: <Kilometers>400000, // Link-budget limited; capped at lunar distance for visualization
    beamwidth: <Degrees>0.07, // 35m dish at X-band
    zoom: ZoomValue.GEO,
    changeObjectInterval: <Milliseconds>20000,
    url: 'https://www.esa.int/Enabling_Support/Operations/ESA_ground_stations',
    country: 'Australia',
    operator: Operators.ESA,
  }),
  ESTCEB: new DetailedSensor({
    objName: 'ESTCEB',
    shortName: 'CEB',
    id: 0,
    name: 'ESTRACK Cebreros Station (DSA 2), Spain',
    uiName: 'Cebreros (DSA 2)',
    system: 'ESTRACK Deep Space Antenna',
    freqBand: 'X/Ka-Band',
    type: SpaceObjectType.GROUND_SENSOR_STATION,
    lat: <Degrees>40.4527,
    lon: <Degrees>-4.3676,
    alt: <Kilometers>0.794,
    minAz: <Degrees>0,
    maxAz: <Degrees>360,
    minEl: <Degrees>6,
    maxEl: <Degrees>90,
    minRng: <Kilometers>100,
    maxRng: <Kilometers>400000,
    beamwidth: <Degrees>0.07,
    zoom: ZoomValue.GEO,
    changeObjectInterval: <Milliseconds>20000,
    url: 'https://www.esa.int/Enabling_Support/Operations/ESA_ground_stations',
    country: 'Spain',
    operator: Operators.ESA,
  }),
  ESTMLG: new DetailedSensor({
    objName: 'ESTMLG',
    shortName: 'MLG',
    id: 0,
    name: 'ESTRACK Malargue Station (DSA 3), Argentina',
    uiName: 'Malargue (DSA 3)',
    system: 'ESTRACK Deep Space Antenna',
    freqBand: 'X/Ka-Band',
    type: SpaceObjectType.GROUND_SENSOR_STATION,
    lat: <Degrees>-35.776,
    lon: <Degrees>-69.3982,
    alt: <Kilometers>1.55,
    minAz: <Degrees>0,
    maxAz: <Degrees>360,
    minEl: <Degrees>6,
    maxEl: <Degrees>90,
    minRng: <Kilometers>100,
    maxRng: <Kilometers>400000,
    beamwidth: <Degrees>0.07,
    zoom: ZoomValue.GEO,
    changeObjectInterval: <Milliseconds>20000,
    url: 'https://www.esa.int/Enabling_Support/Operations/ESA_ground_stations',
    country: 'Argentina',
    operator: Operators.ESA,
  }),
};
